import { useEffect } from 'react'
import { Visibility, VisibilityOff, CheckBoxOutlineBlank } from '@mui/icons-material'
import CheckboxIcon from '@mui/icons-material/CheckBox'
import { TextField, IconButton, InputAdornment, Checkbox, FormControlLabel } from '@mui/material'
import { motion } from 'framer-motion'
import { useHistory } from 'react-router-dom'
import { Button } from '@components/v2/button'
import { cn } from '@global/utils/tailwind'
import { useLogin } from './hooks/useLogin'
import styles from './login.module.css'
import { t } from '@lingui/core/macro'
import { useSsoEnabled } from '@global/hooks/queries/useAuthHintQuery'

export const Login = () => {
  const history = useHistory()

  const ssoEnabled = useSsoEnabled()
  const { setShowPassword, showPassword, loading, loginForm, handleLogin, handleLoginFormChange } =
    useLogin()

  useEffect(() => {
    if (ssoEnabled) {
      history.push('/')
    }
  }, [history, ssoEnabled])

  useEffect(() => {
    // redirect facebook users to meta since they have undergone an organization name change
    if (window.location.host.includes('facebook')) {
      window.location.href = 'https://meta.claravine.com'
    }
  }, [])

  return (
    <motion.div className="auth-login">
      <form onSubmit={handleLogin}>
        <motion.div
          className={styles.form}
          variants={styles.formVariants}
          animate="visible"
          initial="hidden"
        >
          <motion.div variants={styles.fieldVariants} className={styles.formField}>
            <TextField
              label={t`Email`}
              name="email"
              value={loginForm.email || ''}
              onChange={e => {
                handleLoginFormChange(e.target.name, e.target.value)
              }}
              margin="normal"
              variant="outlined"
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </motion.div>
          <motion.div variants={styles.fieldVariants} className={styles.formField}>
            <TextField
              variant="outlined"
              name="password"
              value={loginForm.password || ''}
              onChange={e => {
                handleLoginFormChange(e.target.name, e.target.value)
              }}
              type={showPassword ? 'text' : 'password'}
              label={t`Password`}
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle password visibility"
                      onClick={() => {
                        setShowPassword(!showPassword)
                      }}
                      size="large"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </motion.div>
          <motion.div
            variants={styles.fieldVariants}
            className={styles.formField}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <div style={{ flex: 1 }}>
              <FormControlLabel
                size="small"
                control={
                  <Checkbox
                    checked={loginForm.remember_me}
                    value={loginForm.remember_me}
                    name="remember_me"
                    onChange={(e, val) => {
                      handleLoginFormChange(e.target.name, val)
                    }}
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckboxIcon fontSize="small" />}
                    inputProps={{
                      'aria-label': 'primary checkbox',
                    }}
                  />
                }
                label={t`Remember me`}
                sx={{ fontSize: '12px', color: '#555' }}
              />
            </div>
            <div style={{ flex: 1, textAlign: 'right' }}>
              <Button
                onClick={() => {
                  let url = '/auth/forgot'
                  if (location.href.includes('login=system')) {
                    url += '?login=system'
                  }
                  history.push(url)
                }}
                size="sm"
                variant="text"
              >
                {t`Forgot password?`}
              </Button>
            </div>
          </motion.div>
          <motion.div variants={styles.fieldVariants} className={styles.formField}>
            <Button
              loadingText={t`Verifying...`}
              isLoading={loading}
              variant="primary"
              type="submit"
              className={cn(!loading ? 'px-[30px]' : '')}
            >
              {t`Login`}
            </Button>
          </motion.div>
          <motion.div
            className={[styles.terms, styles.formField]}
            variants={styles.fieldVariants}
            style={{ fontSize: '12px', color: '#555', textAlign: 'center' }}
          >
            <p>
              {t`By logging in, you agree to our`}
              <a
                className={styles.termsLink}
                target="_blank"
                href="https://www.claravine.com/terms-conditions/"
                rel="noreferrer"
              >
                {t`Terms & Conditions`}
              </a>
            </p>
          </motion.div>
        </motion.div>
      </form>
    </motion.div>
  )
}
